import React, {useState, useCallback, useRef, useEffect} from 'react'
import styled from 'styled-components'
import i18n from 'i18next'
import ReactCountryFlag from 'react-country-flag'

import {Select} from '../../Atoms/Forms'
import {UserLanguage} from '../../../state/rest'
import {colors} from '../../../sharedComponents/colors'

const languageCountryMap: Record<UserLanguage, string> = {
  [UserLanguage.en]: 'GB',
  [UserLanguage.fi]: 'FI',
  [UserLanguage.sv]: 'SE',
  [UserLanguage.no]: 'NO'
}

const FlagWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.25);
  background-color: ${colors.system.white};
  padding: 0;
  box-sizing: border-box;
  overflow: visible;
  flex-shrink: 0;
  margin-right: 0.75rem;
  line-height: 0;
`

const TriggerFlagWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.25);
  background-color: ${colors.system.white};
  padding: 0;
  box-sizing: border-box;
  overflow: visible;
  flex-shrink: 0;
  margin-right: 0.4rem;
  line-height: 0;
`

const FlagInner = styled.span`
  position: absolute;
  top: 1px;
  right: 1px;
  bottom: 1px;
  left: 1px;
  border-radius: 50%;
  overflow: hidden;
  display: block;
  line-height: 0;
`

interface Props {
  controller: any
}
const LanguageSelect = ({controller}: Props): any => {
  const fc = controller
  return (
    <Select
      key={`language_select`}
      nativeProps={{
        name: 'language',
        onChange(e) {
          fc.selectLanguage(fc.languageOptions.find((o: any) => o.option === e.target.value)!.id)
        },
        value: fc.selectedLanguage.id
      }}
      id="language"
      options={fc.languageOptions}
    />
  )
}

const LoginLanguageContainer = styled.div`
  text-align: right;
  margin: 2rem;
  margin-left: auto;
  position: relative;
  width: fit-content;
`

const LanguageTrigger = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  color: ${colors.system.white};
`

const LanguageDropdown = styled.div<{isOpen: boolean}>`
  position: absolute;
  right: 0;
  margin-top: 0.5rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: ${props => (props.isOpen ? 'block' : 'none')};
  z-index: 1000;
`

const LanguageOptionRow = styled.div`
  display: flex;
  align-items: center;
`

const LanguageOption = styled.div<{isSelected: boolean}>`
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-weight: ${props => (props.isSelected ? 'bold' : 'normal')};
  background-color: ${props => (props.isSelected ? colors.system.lightGrey_5 : 'transparent')};
  color: ${props => (props.isSelected ? colors.brand.cobalt : 'inherit')};

  &:hover {
    background-color: ${colors.system.lightGrey_5};
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`

export const LoginLanguageSelect = () => {
  const defaultLang = UserLanguage.en
  const [lang, setLang] = useState(defaultLang)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const languageOptions = Object.values(UserLanguage)

  const handleLanguageSelect = (selectedLanguage: UserLanguage) => {
    setLang(selectedLanguage)
    i18n.changeLanguage(selectedLanguage)
    setIsDropdownOpen(false)
  }

  const handleBadgeClick = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isDropdownOpen])

  return (
    <LoginLanguageContainer ref={containerRef}>
      <LanguageTrigger onClick={handleBadgeClick}>
        <TriggerFlagWrapper>
          <FlagInner>
            <ReactCountryFlag
              countryCode={languageCountryMap[lang]}
              svg
              style={{width: '100%', height: '100%', display: 'block', objectFit: 'cover'}}
              aria-label={lang}
            />
          </FlagInner>
        </TriggerFlagWrapper>
        {lang.toUpperCase()}
      </LanguageTrigger>
      <LanguageDropdown isOpen={isDropdownOpen}>
        {languageOptions.map(option => (
          <LanguageOption key={option} isSelected={option === lang} onClick={() => handleLanguageSelect(option)}>
            <LanguageOptionRow>
              <FlagWrapper>
                <FlagInner>
                  <ReactCountryFlag
                    countryCode={languageCountryMap[option]}
                    svg
                    style={{width: '100%', height: '100%', display: 'block', objectFit: 'cover'}}
                    aria-label={getLanguageName(option)}
                  />
                </FlagInner>
              </FlagWrapper>
              {getLanguageName(option)}
            </LanguageOptionRow>
          </LanguageOption>
        ))}
      </LanguageDropdown>
    </LoginLanguageContainer>
  )
}

export function useLanguageSelect(defaultOption?: string, saved?: string) {
  const languageOptions = Object.values(UserLanguage).map(id => ({option: getLanguageName(id), id: id}))
  const defaultIndex = Math.max(
    languageOptions.findIndex(df => df.id === defaultOption),
    0
  )
  const [selected, setSelected] = useState(saved ? saved : languageOptions[defaultIndex].id)

  const selectMethod = useCallback((id: string) => {
    setSelected(id)
  }, [])

  return {selectedLanguage: selected, selectLanguage: selectMethod, languageOptions}
}

function getLanguageName(languageKey: string) {
  switch (languageKey) {
    case UserLanguage.fi: {
      return i18n.t('common:general.lang.finnish', 'Finnish')
    }
    case UserLanguage.sv: {
      return i18n.t('common:general.lang.swedish', 'Swedish')
    }
    case UserLanguage.no: {
      return i18n.t('common:general.lang.norwegian', 'Norwegian')
    }
    default: {
      return i18n.t('common:general.lang.english', 'English')
    }
  }
}

export default LanguageSelect
